import { ParsedUrlQuery } from 'querystring'
import { BUILD, ORDERED_OPTIONS } from '@consts/index'
import { FilterOption } from 'types/filters'
import { Collection } from 'types/collection'
import { ProductFeed } from 'types/product'
import { isSizeFilterType } from '@helpers/filters/isSizeFilterType'

export function getCollectionFiltersState(
  selectedFilterOptions: FilterOption[],
  fitAttribute: string,
  selectedOption: string,
  allowMultiple = false
) {
  const newFilterOptions = [...selectedFilterOptions]

  const filterOptionIndex = selectedFilterOptions.findIndex(
    option =>
      option.type?.toLowerCase() === fitAttribute?.toLowerCase() &&
      (!allowMultiple ||
        option.value?.toLowerCase() === selectedOption?.toLowerCase())
  )

  const newFilterOption = {
    type: fitAttribute,
    position:
      1 +
      ORDERED_OPTIONS.findIndex(
        fitType => fitAttribute === fitType.toLowerCase()
      ),
    value: selectedOption.replace(BUILD, '').trim()
  }
  if (filterOptionIndex === -1) {
    newFilterOptions.push(newFilterOption)
  } else if (allowMultiple) {
    newFilterOptions.splice(filterOptionIndex, 1)
  } else if (
    newFilterOptions[filterOptionIndex].value?.toLowerCase() ===
    selectedOption?.toLowerCase()
  ) {
    newFilterOptions.splice(filterOptionIndex, 1)
  } else {
    newFilterOptions[filterOptionIndex] = { ...newFilterOption }
  }
  return newFilterOptions
}

// Keeps facet filters (colour, fabric, or any future non-size facet type)
// reflected in the URL so collection pages stay shareable/deep-linkable.
// Sizing filters are left alone — those aren't part of this URL contract.
export function getFacetQueryFromFilterOptions(
  query: ParsedUrlQuery,
  previousOptions: FilterOption[],
  nextOptions: FilterOption[]
): ParsedUrlQuery {
  const facetTypes = new Set(
    [...previousOptions, ...nextOptions]
      .filter(option => !isSizeFilterType(option.type))
      .map(option => option.type)
  )

  if (facetTypes.size === 0) return query

  const nextQuery = { ...query }

  facetTypes.forEach(type => {
    const values = nextOptions
      .filter(option => option.type === type)
      .map(option => option.value.toLowerCase())

    if (values.length) {
      nextQuery[type] = values.join(',')
    } else {
      delete nextQuery[type]
    }
  })

  return nextQuery
}

export const filterByProductFeed = (
  collections: Collection[],
  productStyle: string,
  type = 'style'
) => {
  return collections
    .map(collection => {
      const items = collection.items.filter(item => {
        if (type === 'style') {
          return [item.product_feed.style?.toLowerCase()].includes(
            productStyle.toLowerCase()
          )
        }
        if (type === 'group') {
          return [
            item.product_feed.ecom_product_grouping?.toLowerCase()
          ].includes(productStyle.toLowerCase())
        }
        return [item.product_feed.ecom_product_header?.toLowerCase()].includes(
          productStyle.toLowerCase()
        )
      })

      return { ...collection, items }
    })
    .filter(collection => collection.items.length > 0)
}

export const getAvailableProductStyles = (collections: Collection[]) => {
  const filtersByStyle = getProductFeedValues(collections, 'style')
  const newSetFilters = ['All', ...new Set(filtersByStyle)]

  return newSetFilters
}

export const getEcomProductCategoriesByType = (
  collections: Collection[],
  type: keyof ProductFeed
) => {
  const categories = getProductFeedValues(collections, type)

  return ['All', ...new Set(categories)]
}

const getProductFeedValues = (
  collections: Collection[],
  field: keyof ProductFeed
) => {
  const values = collections
    .flatMap(collection =>
      collection.items.map(item => item.product_feed[field])
    )
    .filter(Boolean)

  return values
}
